var _dec, _class, _Selectable;
/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import { Component } from 'react';
import keycode from 'keycode';
import { isActiveElement } from '@instructure/ui-dom-utils';
import { createChainedFunction } from '@instructure/ui-utils';
import { logError as error } from '@instructure/console';
import { allowedProps } from "./props.js";
import { withDeterministicId } from '@instructure/ui-react-utils';

/**
---
category: components/utilities
tags: autocomplete, typeahead, combobox, dropdown, search
---
**/
let Selectable = (_dec = withDeterministicId(), _dec(_class = (_Selectable = class Selectable extends Component {
  constructor(...args) {
    super(...args);
    this._id = this.props.id || this.props.deterministicId();
    this._listId = `${this._id}-list`;
    this._descriptionId = `${this._id}-description`;
    this._trigger = null;
    this.isSelectedOption = id => {
      const selectedOptionId = this.props.selectedOptionId;
      if (Array.isArray(selectedOptionId)) {
        return selectedOptionId.indexOf(id) > -1;
      }
      return selectedOptionId === id;
    };
    this.handleOpenClose = event => {
      const _this$props = this.props,
        isShowingOptions = _this$props.isShowingOptions,
        onRequestShowOptions = _this$props.onRequestShowOptions,
        onRequestHideOptions = _this$props.onRequestHideOptions;
      event.preventDefault();
      if (isShowingOptions) {
        onRequestHideOptions === null || onRequestHideOptions === void 0 ? void 0 : onRequestHideOptions(event);
      } else {
        if (!isActiveElement(this._trigger)) {
          ;
          this._trigger.focus();
        }
        onRequestShowOptions === null || onRequestShowOptions === void 0 ? void 0 : onRequestShowOptions(event);
      }
    };
    this.handleKeyDown = event => {
      const _this$props2 = this.props,
        isShowingOptions = _this$props2.isShowingOptions,
        highlightedOptionId = _this$props2.highlightedOptionId,
        onRequestHighlightOption = _this$props2.onRequestHighlightOption,
        onRequestHighlightFirstOption = _this$props2.onRequestHighlightFirstOption,
        onRequestHighlightLastOption = _this$props2.onRequestHighlightLastOption,
        onRequestSelectOption = _this$props2.onRequestSelectOption;
      const key = keycode.names[event.keyCode];
      switch (key) {
        case 'space':
          if (!isShowingOptions) {
            // if options hidden, show them
            this.handleOpenClose(event);
          }
          break;
        case 'enter':
          if (highlightedOptionId) {
            // select highlighted option
            event.preventDefault();
            onRequestSelectOption === null || onRequestSelectOption === void 0 ? void 0 : onRequestSelectOption(event, {
              id: highlightedOptionId
            });
          }
          break;
        case 'down':
          event.preventDefault();
          if (isShowingOptions) {
            // if options showing, change highlight
            onRequestHighlightOption === null || onRequestHighlightOption === void 0 ? void 0 : onRequestHighlightOption(event, {
              direction: 1
            });
          } else {
            // otherwise, show options
            this.handleOpenClose(event);
          }
          break;
        case 'up':
          event.preventDefault();
          if (isShowingOptions) {
            // if options showing, change highlight
            onRequestHighlightOption === null || onRequestHighlightOption === void 0 ? void 0 : onRequestHighlightOption(event, {
              direction: -1
            });
          } else {
            // otherwise, show options
            this.handleOpenClose(event);
          }
          break;
        case 'home':
          if (isShowingOptions) {
            // if options showing, highlight first option
            event.preventDefault();
            onRequestHighlightFirstOption === null || onRequestHighlightFirstOption === void 0 ? void 0 : onRequestHighlightFirstOption(event);
          }
          break;
        case 'end':
          if (isShowingOptions) {
            // if options showing, highlight last option
            event.preventDefault();
            onRequestHighlightLastOption === null || onRequestHighlightLastOption === void 0 ? void 0 : onRequestHighlightLastOption(event);
          }
          break;
      }
    };
    this.handleKeyUp = event => {
      const isShowingOptions = this.props.isShowingOptions;
      const key = keycode.names[event.keyCode];
      if (key === 'esc') {
        if (isShowingOptions) {
          // if options showing, hide them
          this.handleOpenClose(event);
        }
      }
    };
  }
  render() {
    const _this$props3 = this.props,
      isShowingOptions = _this$props3.isShowingOptions,
      highlightedOptionId = _this$props3.highlightedOptionId,
      onRequestHighlightOption = _this$props3.onRequestHighlightOption,
      onRequestSelectOption = _this$props3.onRequestSelectOption,
      children = _this$props3.children,
      _this$props3$render = _this$props3.render,
      render = _this$props3$render === void 0 ? children : _this$props3$render;
    if (typeof render === 'function') {
      return render({
        getRootProps: ({
          onMouseDown,
          ...rest
        } = {}) => {
          return {
            onMouseDown: createChainedFunction(event => {
              // if we call preventDefault, label can't be selected and copied, so we only call it when the options are shown
              if (event.target !== this._trigger && isShowingOptions) {
                event.preventDefault(); // prevent trigger from losing focus
              }
            }, onMouseDown),
            ...rest
          };
        },
        getLabelProps: props => {
          return {
            htmlFor: this._id,
            ...props
          };
        },
        getTriggerProps: ({
          ref,
          onKeyDown,
          onKeyUp,
          onClick,
          ...rest
        } = {}) => {
          return {
            id: this._id,
            ref: createChainedFunction(ref, el => this._trigger = el),
            'aria-haspopup': 'listbox',
            'aria-expanded': isShowingOptions,
            'aria-controls': isShowingOptions ? this._listId : void 0,
            'aria-describedby': this._descriptionId,
            'aria-activedescendant': isShowingOptions ? highlightedOptionId : void 0,
            onKeyDown: createChainedFunction(this.handleKeyDown, onKeyDown),
            onKeyUp: createChainedFunction(this.handleKeyUp, onKeyUp),
            onClick: createChainedFunction(this.handleOpenClose, onClick),
            ...rest
          };
        },
        getInputProps: ({
          readOnly,
          ...rest
        } = {}) => {
          return {
            role: 'combobox',
            'aria-autocomplete': readOnly ? 'none' : 'both',
            autoComplete: 'off',
            readOnly,
            ...rest
          };
        },
        getListProps: ({
          onMouseDown,
          onClick,
          ...rest
        } = {}) => {
          return {
            id: this._listId,
            role: 'listbox',
            onMouseDown: createChainedFunction(event => {
              event.preventDefault(); // prevent trigger from losing focus
            }, onMouseDown),
            onClick: createChainedFunction(event => {
              // prevent synthetic event from firing on the document
              // this event could inadvertently close a parent dialog
              event.stopPropagation();
              event.nativeEvent.stopImmediatePropagation();
            }, onClick),
            ...rest
          };
        },
        getOptionProps: ({
          id,
          onMouseOver,
          onClick,
          ...rest
        } = {}) => {
          error(Boolean(id), `[Selectable] Must provide id for each option via \`getOptionProps\`.`);
          return {
            id,
            role: 'option',
            'aria-selected': this.isSelectedOption(id) ? 'true' : 'false',
            onClick: createChainedFunction(event => {
              onRequestSelectOption === null || onRequestSelectOption === void 0 ? void 0 : onRequestSelectOption(event, {
                id
              });
            }, onClick),
            onMouseOver: createChainedFunction(event => {
              onRequestHighlightOption === null || onRequestHighlightOption === void 0 ? void 0 : onRequestHighlightOption(event, {
                id
              });
            }, onMouseOver),
            ...rest
          };
        },
        getDisabledOptionProps: props => {
          return {
            'aria-disabled': 'true',
            ...props
          };
        },
        getDescriptionProps: props => {
          return {
            id: this._descriptionId,
            ...props
          };
        }
      });
    } else {
      return null;
    }
  }
}, _Selectable.displayName = "Selectable", _Selectable.allowedProps = allowedProps, _Selectable.defaultProps = {
  isShowingOptions: false
}, _Selectable)) || _class);
export default Selectable;
export { Selectable };